import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarCheck, Search } from "lucide-react";
import { useState } from "react";
import {
  docVerificationState,
  type OpsDocReviewPage,
} from "../../shared/operations-docs-maintenance";
import { ContentLoading } from "../components/ContentLoading";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { getDocReviews } from "./docs-maintenance-api";
import type { OperationsNavigate } from "./navigation";

function ReviewList({
  pages,
  openPage,
}: {
  pages: OpsDocReviewPage[];
  openPage: (id: string) => void;
}) {
  const [owner, setOwner] = useState("");
  const [status, setStatus] = useState("attention");
  const [search, setSearch] = useState("");
  const owners = [
    ...new Map(pages.map((page) => [page.ownerEmployeeId, page.ownerDisplayName])),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const matching = pages
    .filter((page) => {
      const state = docVerificationState(page);
      return (
        (!owner || page.ownerEmployeeId === owner) &&
        (status === "all" || (status === "attention" ? state !== "Verified" : state === status)) &&
        `${page.title} ${page.spaceName}`.toLocaleLowerCase().includes(search.toLocaleLowerCase())
      );
    })
    .sort((a, b) => {
      const priority = { "Needs review": 0, "Not verified": 1, Verified: 2 };
      return (
        priority[docVerificationState(a)] - priority[docVerificationState(b)] ||
        (a.nextVerificationAt ?? "9999").localeCompare(b.nextVerificationAt ?? "9999") ||
        a.title.localeCompare(b.title)
      );
    });
  return (
    <div className="space-y-4">
      <ReviewFilters
        owners={owners}
        owner={owner}
        setOwner={setOwner}
        status={status}
        setStatus={setStatus}
        search={search}
        setSearch={setSearch}
      />
      <p className="text-xs text-muted-foreground" role="status">
        <span className="tabular-nums">{matching.length}</span>{" "}
        {matching.length === 1 ? "document" : "documents"}
      </p>
      {matching.length ? (
        <ul className="divide-y">
          {matching.map((page) => (
            <ReviewRow key={page.id} page={page} openPage={openPage} />
          ))}
        </ul>
      ) : (
        <div className="rounded-sm border border-dashed p-8 text-center">
          <CalendarCheck className="mx-auto mb-3 size-6 text-muted-foreground" />
          <p className="font-semibold">No documents match these filters</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Change the owner, status or search to see more documents.
          </p>
        </div>
      )}
    </div>
  );
}

export function DocsReviewsDialog({ navigate }: { navigate: OperationsNavigate }) {
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: ["ops", "docs", "reviews"],
    queryFn: getDocReviews,
    enabled: open,
    staleTime: 0,
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="ghost" size="sm" />}>
        <CalendarCheck /> Reviews
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Document reviews</DialogTitle>
          <DialogDescription>
            Keep your team’s knowledge current. Open a document, review its content, then mark it
            reviewed.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {query.isPending ? (
            <ContentLoading label="Loading document reviews…" variant="rows" />
          ) : null}
          {query.error ? (
            <div className="space-y-3">
              <FieldError>{query.error.message}</FieldError>
              <Button variant="outline" onClick={() => void query.refetch()}>
                Try again
              </Button>
            </div>
          ) : null}
          {query.data && !query.error ? (
            <ReviewList
              pages={query.data.pages}
              openPage={(id) => {
                navigate(`/docs/${id}`);
                setOpen(false);
              }}
            />
          ) : null}
        </DialogBody>
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />}>Done</DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewRow({ page, openPage }: { page: OpsDocReviewPage; openPage: (id: string) => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => {
          openPage(page.id);
        }}
        className="flex min-h-20 w-full items-center gap-3 rounded-sm px-2 py-4 text-left hover:bg-muted focus-visible:outline-2 focus-visible:outline-link"
      >
        <div className="min-w-0 flex-1 space-y-1">
          <p className="break-words text-sm font-semibold">{page.title}</p>
          <p className="text-xs text-muted-foreground">
            {page.spaceName} · {page.ownerDisplayName}
          </p>
          <p className="text-xs text-muted-foreground">
            {page.verificationStale
              ? "Changed since last review"
              : page.verifiedAt
                ? `Last reviewed ${page.verifiedAt}`
                : "No review recorded"}
            {page.nextVerificationAt ? ` · Next review ${page.nextVerificationAt}` : ""}
          </p>
        </div>
        <Badge
          variant="secondary"
          className={
            docVerificationState(page) === "Needs review"
              ? "border-warning bg-warning-background text-warning"
              : undefined
          }
        >
          {docVerificationState(page)}
        </Badge>
        <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
      </button>
    </li>
  );
}

function ReviewFilters({
  owners,
  owner,
  setOwner,
  status,
  setStatus,
  search,
  setSearch,
}: {
  owners: [string, string][];
  owner: string;
  setOwner: (value: string) => void;
  status: string;
  setStatus: (value: string) => void;
  search: string;
  setSearch: (value: string) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
      <div className="relative">
        <Search className="pointer-events-none absolute top-3 left-3 size-4 text-muted-foreground" />
        <Input
          className="pl-9"
          aria-label="Find a document to review"
          placeholder="Find a document…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      </div>
      <select
        aria-label="Filter reviews by owner"
        value={owner}
        onChange={(event) => {
          setOwner(event.target.value);
        }}
        className="h-10 min-w-0 rounded-sm border bg-background px-3 text-sm"
      >
        <option value="">All owners</option>
        {owners.map(([id, name]) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))}
      </select>
      <select
        aria-label="Filter reviews by status"
        value={status}
        onChange={(event) => {
          setStatus(event.target.value);
        }}
        className="h-10 rounded-sm border bg-background px-3 text-sm"
      >
        <option value="attention">Needs attention</option>
        <option value="Needs review">Needs review</option>
        <option value="Not verified">Not verified</option>
        <option value="Verified">Verified</option>
        <option value="all">All documents</option>
      </select>
    </div>
  );
}
